import { Link } from "react-router-dom";
import {
  BarChart3,
  Zap,
  Target,
  ArrowRight,
  Bot,
  Eye,
  Search,
  Settings,
  BarChart,
} from "lucide-react";
import { getSaasUrl } from "../utils/saasUrl";

const DynamicPricing = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">Dynamic Pricing</span>
              <br />
              Intelligence
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maximize revenue with AI-powered pricing that adapts to market
              conditions, competitor moves, and demand patterns in real-time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/dynamic-price-dashboard.png"
                alt="Price Optimization Dashboard"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-violet-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Zap className="h-6 w-6 text-violet-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Real-time Optimization
                  </h3>
                  <p className="text-gray-600">
                    Prices adjust automatically based on demand, inventory, and
                    competitor analysis.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-violet-600/70 rounded-lg flex items-center justify-center bg-white">
                  <BarChart3 className="h-6 w-6 text-violet-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Competitor Intelligence
                  </h3>
                  <p className="text-gray-600">
                    Monitor and analyze a wide range of competitors across
                    multiple channels and markets.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-violet-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Target className="h-6 w-6 text-violet-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Strategic Rules
                  </h3>
                  <p className="text-gray-600">
                    Set custom pricing rules and constraints to align with
                    business objectives.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Key Features</span>
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need for intelligent pricing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "AI-Powered Algorithms",
                description:
                  "Machine learning models that continuously improve pricing accuracy",
                icon: <Bot className="h-8 w-8 text-pink-600" />,
              },
              {
                title: "Competitor Tracking",
                description:
                  "Monitor competitor prices across all channels and marketplaces",
                icon: <Eye className="h-8 w-8 text-pink-600" />,
              },
              {
                title: "Find Competitors",
                description:
                  "Automatically discover and track new competitors in your market",
                icon: <Search className="h-8 w-8 text-pink-600" />,
              },
              {
                title: "Custom Rules Engine",
                description:
                  "Set business rules and constraints for automated pricing",
                icon: <Settings className="h-8 w-8 text-pink-600" />,
              },
              {
                title: "Real-time Updates",
                description: "Instant price changes across all sales channels",
                icon: <Zap className="h-8 w-8 text-pink-600" />,
              },
              {
                title: "Daily Reports",
                description:
                  "Comprehensive daily analytics and performance insights",
                icon: <BarChart className="h-8 w-8 text-pink-600" />,
              },
            ].map((feature, index) => (
              <div key={index} className="card">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Optimize Your Pricing?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Trusted by growing companies to optimize their pricing strategy and
            maximize revenue.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getSaasUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DynamicPricing;
